<template>
  <!-- Screenshot preview + eyedropper. Users hover (desktop) or long-press
       (mobile) the screenshot to sample a color, which is emitted as `sampled`.
       Emits `loaded` once the real screenshot has rendered. -->
  <div
    class="mt-10 rounded-lg transition-[margin-top] duration-500 ease-in-out"
    :class="{ 'mt-3': screenshotLoaded }"
  >
    <div
      class="relative border-x border-t rounded-t-md border-gray-800"
      :class="{ 'overflow-hidden': screenshotAnimation && screenshotLoaded, 'border-b rounded-b-md': screenshotLoaded }"
    >
      <!-- Placeholder and real screenshot share one box. The placeholder is an
           overlay, so leaving the DOM costs no layout; the box's own height is
           the placeholder's aspect ratio until the screenshot arrives, then
           animates to the screenshot's height (see the watcher below). Without
           this the two stack in flow and the frame doubles in height for the
           length of the crossfade before snapping back. -->
      <div
        ref="frame"
        class="relative overflow-hidden rounded-md"
        :class="{ 'aspect-[2760/897]': !screenshotLoaded }"
      >
        <transition
          enter-active-class="transition-opacity duration-500"
          leave-active-class="transition-opacity duration-500"
          enter-from-class="opacity-0"
          enter-to-class="opacity-20"
          leave-from-class="opacity-20"
          leave-to-class="opacity-0"
        >
          <img
            v-if="screenshotLoaded === false"
            :src="placeholder"
            alt=""
            width="2760"
            height="897"
            class="absolute inset-0 h-full w-full rounded-md opacity-20 blur-sm"
          />
        </transition>

        <canvas ref="canvas" class="hidden"></canvas>

        <!-- An eyedropper surface, not a picture to save: no selection, no
             dragging, no iOS "Save Image" callout on long-press. -->
        <img
          ref="image"
          class="max-w-full cursor-crosshair select-none rounded-md transition-opacity duration-500 ease-in-out [-webkit-touch-callout:none]"
          :class="[screenshotLoaded ? 'opacity-100' : 'opacity-0', { 'blur-md': screenshotAnimation }]"
          :src="src"
          alt=""
          draggable="false"
          crossorigin="anonymous"
          @contextmenu.prevent
          @mousemove="handleMouseMove"
          @mouseleave="hideMagnifier"
          @click="handleClick"
          @load="$emit('loaded')"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        />
      </div>

      <!-- The magnifier: a live swatch of the colour under the pointer. -->
      <div
        v-if="showMagnifier"
        class="pointer-events-none absolute w-32 min-w-max overflow-hidden rounded border-2 border-white bg-white shadow-lg shadow-black/50"
        :style="{ top: magnifierY + 'px', left: magnifierX + 'px' }"
        aria-hidden="true"
      >
        <ColorSwatch :color="selectedColor" size="xs" />
        <p class="bg-white p-2 text-xs font-semibold text-gray-800">{{ selectedColor }}</p>
        <p class="flex min-w-max items-center gap-1 bg-white p-2 pt-1 text-xs font-semibold text-gray-600">
          <CursorArrowRaysIcon class="size-4" />
          {{ isMobile ? 'Release to sample' : 'Click to sample' }}
        </p>
      </div>

      <!-- Gradient Overlay -->
      <transition
        enter-active-class="transition-opacity duration-500"
        leave-active-class="transition-opacity duration-500"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="screenshotLoaded !== true" class="absolute -bottom-3 left-0 w-[110%] h-20 bg-gradient-to-t from-gray-950 to-transparent -ml-2"></div>
      </transition>

      <div v-if="screenshotPromtShow === true" class="absolute left-1/2 top-0 flex size-max -translate-x-1/2 items-center gap-1 rounded-b-md bg-gray-900 px-2.5 py-1 text-xs text-gray-400">
        <div v-if="!screenshotAnimation">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="size-3.5" aria-hidden="true">
            <path d="M17.4714 13.4473L10.3901 6.38467L11.8032 4.96944C12.5839 5.74907 13.8509 5.74798 14.6296 4.96419L16.2145 3.45074C16.8427 2.82098 17.6808 2.52914 18.5036 2.57396C20.1672 2.66459 21.4413 4.09402 21.3522 5.72959C21.3128 6.45152 21.0035 7.14773 20.4643 7.68919L18.8794 9.20164C18.1007 9.98444 18.1009 11.2493 18.8835 12.0311L17.4714 13.4473Z" />
            <path d="M7.22915 17.5933L15.9338 8.9137L16.9013 9.8784L8.11705 18.6508C7.16908 19.6001 6.59031 18.7831 5.35867 19.6274C5.19383 19.7395 5.01968 19.784 4.85529 19.7751C4.44532 19.7527 4.09518 19.3984 4.11974 18.9475C4.12784 18.7988 4.17891 18.6401 4.28006 18.4787C5.01375 17.2934 4.29045 16.6806 5.21442 15.7574L13.9987 6.98362L14.9663 7.94832L6.26162 16.6286C6.03434 16.857 6.08236 17.5329 5.94445 17.9044C6.31536 17.7645 7.00188 17.8217 7.22915 17.5933Z" />
            <rect x="14.1211" y="8.46484" width="2" height="13" transform="rotate(45 14.1211 8.46484)" />
          </svg>
        </div>
        <SpinnerIcon v-if="screenshotAnimation" class="ml-1 h-3 w-3 text-white" />
        <p v-if="screenshotAnimation">Screenshot loading</p>
        <p v-else-if="!screenshotLoaded">Screenshot will appear here</p>
        <p v-else-if="isMobile">Tap &amp; hold to add colors</p>
        <p v-else>Hover to add colors</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, watch, nextTick } from 'vue'
import { CursorArrowRaysIcon } from '@heroicons/vue/16/solid'
import ColorSwatch from '~/components/atoms/ColorSwatch.vue'
import SpinnerIcon from '~/components/atoms/SpinnerIcon.vue'
import { useColorSampler } from '~/composables/useColorSampler'
import { animateHeight } from '~/utils/motion'
import placeholder from '~/assets/img/screenshot.png'

const props = defineProps({
  src: { type: String, default: '' },
  screenshotLoaded: { type: Boolean, default: false },
  screenshotAnimation: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false },
})
const emit = defineEmits(['sampled', 'loaded'])

const {
  canvas,
  image,
  selectedColor,
  showMagnifier,
  magnifierX,
  magnifierY,
  screenshotPromtShow,
  handleMouseMove,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  handleClick,
  hideMagnifier,
  setDownloadedImage,
} = useColorSampler({
  screenshotAnimation: toRef(props, 'screenshotAnimation'),
  onSampled: (color) => emit('sampled', color),
})

// The frame's height changes in one step when the real screenshot takes over
// from the placeholder's aspect ratio. Measure before the DOM updates (watchers
// are pre-flush) and after, then tween between the two so the panel grows into
// its new size instead of jumping.
const frame = ref(null)

watch(
  () => props.screenshotLoaded,
  async () => {
    const el = frame.value
    if (!el) return
    const from = el.offsetHeight
    await nextTick()
    animateHeight(el, from, el.offsetHeight)
  },
)

// When the screenshot URL changes, (re)initialize the sampling canvas. Also
// covers the "same cached src" case where the <img> won't refire `load`: if the
// element is already complete, signal loaded so the spinner clears.
watch(
  () => props.src,
  (newSrc) => {
    if (!newSrc) return
    setDownloadedImage(newSrc)
    nextTick(() => {
      if (image.value?.complete && image.value.naturalWidth > 0) emit('loaded')
    })
  },
  { immediate: true },
)
</script>
